import { PortableText, type PortableTextComponents } from '@portabletext/react';
import { urlFor } from '@/lib/sanity';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PortableTextRendererProps {
  value: any[];
}

const components: PortableTextComponents = {
  block: {
    h1: ({ children }) => (
      <h1 className="text-4xl md:text-5xl font-bold mt-12 mb-6 text-foreground tracking-tight">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl font-bold mt-10 mb-4 text-foreground border-b border-border pb-3">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl font-semibold mt-8 mb-3 text-primary">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg font-semibold mt-6 mb-2 text-foreground/80 uppercase tracking-wide">
        {children}
      </h4>
    ),
    normal: ({ children }) => (
      <p className="text-base md:text-lg leading-relaxed mb-6 text-foreground/85">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary pl-6 py-4 my-8 italic text-lg text-muted-foreground bg-muted/50 rounded-r-lg">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-outside ml-6 mb-6 space-y-3 text-foreground/85 text-base md:text-lg leading-relaxed">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-outside ml-6 mb-6 space-y-3 text-foreground/85 text-base md:text-lg leading-relaxed">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="pl-2">{children}</li>,
    number: ({ children }) => <li className="pl-2">{children}</li>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-muted px-2 py-1 rounded-md text-sm font-mono text-primary">
        {children}
      </code>
    ),
    link: ({ value, children }) => {
      const href = value?.href || '';
      const isInternal = href.startsWith('/');
      
      if (isInternal) {
        return (
          <Link to={href} className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
            {children}
          </Link>
        );
      }
      
      return (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
        >
          {children}
        </a>
      );
    },
  },
  types: {
    image: ({ value }) => {
      if (!value?.asset) return null;
      
      return (
        <figure className="my-10">
          <img
            src={urlFor(value).width(900).quality(90).url()}
            alt={value.alt || ''}
            className="rounded-xl w-full shadow-lg"
          />
          {value.caption && (
            <figcaption className="text-center text-sm text-muted-foreground mt-3 italic">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    code: ({ value }) => (
      <pre className="bg-slate-900 text-slate-100 p-6 rounded-xl overflow-x-auto my-8 shadow-lg">
        <code className="text-sm font-mono leading-relaxed">
          {value.code}
        </code>
      </pre>
    ),
    callToAction: ({ value }) => (
      <div className={cn(
        "my-8 p-6 rounded-xl border",
        value.style === 'primary' && "bg-primary/5 border-primary/20",
        value.style === 'secondary' && "bg-muted border-border",
        value.style === 'outline' && "bg-transparent border-border"
      )}>
        {value.heading && (
          <h4 className="text-lg font-semibold mb-2 text-foreground">{value.heading}</h4>
        )}
        {value.text && (
          <p className="text-muted-foreground mb-4">{value.text}</p>
        )}
        <Button 
          asChild
          variant={value.style === 'primary' ? 'default' : value.style === 'outline' ? 'outline' : 'secondary'}
        >
          <Link to={value.buttonUrl}>{value.buttonText}</Link>
        </Button>
      </div>
    ),
  },
};

export function PortableTextRenderer({ value }: PortableTextRendererProps) {
  if (!value || !Array.isArray(value)) {
    return null;
  }
  
  return <PortableText value={value} components={components} />;
}
